import React from "react";
import { Link } from "react-router-dom";
import tree from "../img/tree.webp";
import asset from "../img/asset.webp";
import linkedinIcon from "../assets/footer_icone/linkin.svg";
import whatsappIcon from "../assets/footer_icone/whatsapp.svg";
import gmailIcon from "../assets/footer_icone/gmail.svg";
import locationIcon from "../assets/footer_icone/location.png";
import mailIcon from "../assets/footer_icone/mail-icon.png";
import phoneIcon from "../assets/footer_icone/phone-icon.png";
import { useCmsPage } from "../hooks/useCmsPage";
import footerFallbackContent from "../lib/fallbackContent/footer";
import { mergeCmsSection } from "../lib/mergeCmsSection";
import CmsPageLoader from "./CmsPageLoader";
import { resolveCmsImage } from "../lib/cmsAssets";
import { apiBaseUrl } from "../lib/cmsApi";

const Footer = ({ tightTop = false }) => {
  const { status, sections } = useCmsPage("footer", footerFallbackContent);
  const main = mergeCmsSection(sections?.main, footerFallbackContent.main);

  const topPad = tightTop
    ? "pt-10 sm:pt-12 lg:pt-14"
    : "pt-28 sm:pt-36 lg:pt-40";

  if (status === "loading") {
    return <CmsPageLoader label="Loading footer…" />;
  }

  const treeLogo = resolveCmsImage(main.treeImageKey, apiBaseUrl) || tree;
  const assetLogo = resolveCmsImage(main.logoImageKey, apiBaseUrl) || asset;

  const targetEmail = main.email || "care@assetsociety.ngo";
  const phoneHref = main.phone
    ? `tel:${String(main.phone).replace(/[^\d+]/g, "")}`
    : "tel:+918879878766";
  const gmailWebComposeUrl =
    main.gmailUrl && !main.gmailUrl.startsWith("mailto:")
      ? main.gmailUrl
      : `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(targetEmail)}`;

  const socialLinks = [
    {
      href: main.linkedinUrl || "https://www.linkedin.com/",
      label: "LinkedIn",
      icon: linkedinIcon,
    },
    {
      href: main.whatsappUrl || "https://wa.me/918879878766",
      label: "WhatsApp",
      icon: whatsappIcon,
    },
    {
      href: gmailWebComposeUrl,
      label: "Gmail",
      icon: gmailIcon,
    },
  ];

  const navLinks = [
    { to: "/about", label: "About Us" },
    { to: "/what-we-do", label: "What We Do" },
    { to: "/who-we-empower", label: "Who We Empower" },
    { to: "/contact-us", label: "Partner With Us" },
  ];

  return (
    <footer
      className={`relative z-0 w-full bg-[#faf8f4] px-4 pb-8 sm:px-6 lg:px-12 xl:px-16 ${topPad}`}
    >
      <div className="mx-auto grid max-w-6xl gap-10 border-b border-slate-200 pb-10 md:grid-cols-[1.35fr_auto_1.15fr] md:items-start md:gap-0">
        {/* Left — logo + copy + socials (matches design) */}
        <div className="md:pr-8 lg:pr-12">
          <div className="flex items-start gap-3 sm:gap-4">
            <img
              src={treeLogo}
              alt=""
              width={160}
              height={160}
              loading="lazy"
              className="h-[88px] w-[88px] shrink-0 object-contain sm:h-[110px] sm:w-[110px] lg:h-[128px] lg:w-[128px]"
              aria-hidden
            />
            <div className="min-w-0 pt-1 sm:pt-2">
              <Link to="/" onClick={() => window.scrollTo(0, 0)} className="inline-block">
                <img
                  src={assetLogo}
                  alt="A.S.S.E.T"
                  width={180}
                  height={40}
                  loading="lazy"
                  className="h-7 w-auto sm:h-8 lg:h-9"
                />
              </Link>
              <p className="mt-3 max-w-[260px] text-[13px] leading-relaxed text-slate-600 sm:mt-4 sm:max-w-[280px] sm:text-[14px]">
                {main.description}
              </p>
              <div className="mt-4 flex items-center gap-3 sm:mt-5">
                {socialLinks.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={item.label}
                    className="inline-flex h-8 w-8 items-center justify-center transition hover:opacity-80"
                  >
                    <img
                      src={item.icon}
                      alt=""
                      className="h-6 w-6 object-contain sm:h-7 sm:w-7"
                    />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Center — nav with vertical rules */}
        <div className="flex md:border-x md:border-slate-200 md:px-8 lg:px-12">
          <nav
            className="flex flex-col gap-3.5 text-[15px] text-slate-600"
            aria-label="Footer"
          >
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="transition hover:text-[#1565D8]"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* Right — contact with provided icons */}
        <div className="flex flex-col gap-5 text-[14px] text-slate-600 sm:text-[15px] md:pl-8 lg:pl-12">
          <div className="flex items-start gap-3">
            <img
              src={locationIcon}
              alt=""
              className="mt-0.5 h-5 w-5 shrink-0 object-contain"
              aria-hidden
            />
            <div className="min-w-0">
              <p className="font-semibold text-slate-700">
                {main.addressLabel || "Communication Address:"}
              </p>
              <p className="mt-1.5 max-w-xs leading-relaxed">{main.address}</p>
            </div>
          </div>

          <a
            href={gmailWebComposeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 transition hover:text-[#1565D8]"
          >
            <img
              src={mailIcon}
              alt=""
              className="h-4 w-5 shrink-0 object-contain"
              aria-hidden
            />
            <span>{main.email}</span>
          </a>

          <a
            href={phoneHref}
            className="flex items-center gap-3 transition hover:text-[#1565D8]"
          >
            <img
              src={phoneIcon}
              alt=""
              className="h-5 w-5 shrink-0 object-contain"
              aria-hidden
            />
            <span>{main.phone}</span>
          </a>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-2 pt-6 text-center text-[12px] text-[#183B56] sm:text-[13px]">
        <span>{main.copyright || "Copyright © ASSET Society | All Rights Reserved"} | </span>
        <a href={main.termsUrl || "/terms"} className="underline underline-offset-2 hover:opacity-80">
          Terms and Conditions
        </a>
        <span> | </span>
        <a href={main.privacyUrl || "/privacy"} className="underline underline-offset-2 hover:opacity-80">
          Privacy Policy
        </a>
      </div>
    </footer>
  );
};

export default Footer;
